import * as React from 'react'
import type {
  PricingBlock,
  ProposalDocumentDto,
  ProseBlock,
} from '@lib/dto/proposal-document.dto'
import { planLabel, type OfferedProposalPlan } from '@lib/enums/proposal-plan.enum'
import { formatMoneyAmount, parseMoney } from '@lib/helpers/money'
import { formatTaxRatePercent } from '@lib/helpers/tax'
import {
  irrigationScheduleBlocks,
  pricingRowMatches,
  type ProposalSelection,
  type SelectionOutcome,
} from '@lib/helpers/proposal-selection'
import {
  applyAdjustment,
  leftOutWork,
  type AdjustmentChange,
  type AdjustmentPatch,
  type PlanFigures,
} from '@lib/helpers/proposal-plans'
import { UNIDENTIFIED_LABEL } from '@lib/enums/tree-mark.enum'
import { SEASON_LABELS } from '@lib/enums/season.enum'
import { cn } from '@/lib/utils'
import type { Choosing } from '@/proposal-document/choosing'
import {
  areaChoices,
  irrigationKindChoices,
  jobChoices,
  problemChoices,
  scheduleRepairs,
  scheduleTrees,
  siteChoices,
  speciesChoices,
  zoneChoices,
  type Choice,
} from '@/proposal-document/choices'
import { splitHeading } from '@/proposal-document/text'

/**
 * Choosing what to buy: a plan first, adjustments second, the price always in
 * view.
 *
 * The proposal bids everything the arborist found. Most boards pick one of a
 * few plans and stop; the ones with their own reasons (an area that is not in
 * this year's budget, a kind of work the landscape committee does not want)
 * open "Adjust what's included". Every adjustment is then said in words with
 * what it is worth and an undo, because when several people touch a bid each
 * change is a decision somebody will be asked about.
 */

/** "$18,640" from cents, whole dollars: figures to compare, not to invoice. */
export function wholeDollars(cents: number): string {
  return Math.round(cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  })
}

function cents(amount: string | null): number {
  return parseMoney(amount) ?? 0
}

/** "12 trees", "3 repairs", or "12 trees · 3 repairs". */
export function workCount(trees: number, repairs: number): string {
  const treeWords = `${String(trees)} ${trees === 1 ? 'tree' : 'trees'}`
  const repairWords = `${String(repairs)} ${repairs === 1 ? 'repair' : 'repairs'}`
  if (repairs === 0) return treeWords
  if (trees === 0) return repairWords
  return `${treeWords} · ${repairWords}`
}

/** "8.25%" or null when this proposal charges no tax. */
export function taxRateLabel(rate: string | null): string | null {
  if (rate === null) return null
  const formatted = formatTaxRatePercent(rate)
  return formatted === null || formatted === '0' ? null : `${formatted}%`
}

/**
 * What a plan does, in a line. ALL reads differently once work is optional,
 * and Hazards first names what it takes: trees, repairs, or both.
 */
export function planBlurb(figure: PlanFigures, hasOptional: boolean): string {
  switch (figure.plan) {
    case 'HAZARDS':
      return `${hazardsTaken(figure)} rated Serious or Critical. Everything else waits.`
    case 'CORE':
      return 'Everything we recommend, done in one visit.'
    case 'ALL':
      return hasOptional
        ? 'What we recommend, plus the work we offer as optional.'
        : 'Everything we recommend, done in one visit.'
  }
}

/** What "Adjust what's included" can leave out, for the work this bids. */
function adjustHint(document: ProposalDocumentDto, hasIrrigation: boolean): string {
  if (!hasIrrigation) return 'Leave out an area, a kind of work, a problem or a single tree.'
  if (!document.includesTrees) {
    return 'Leave out an area, a kind of repair, a zone, or the irrigation altogether.'
  }
  return 'Leave out an area, a kind of work, a problem, a single tree, or irrigation repairs.'
}

function hazardsTaken(figure: PlanFigures): string {
  if (figure.repairCount === 0) return 'The trees'
  return figure.treeCount === 0 ? 'The repairs' : 'The trees and repairs'
}

/** The plan's role in a word or three, above its name. */
export function planKicker(plan: OfferedProposalPlan, hasOptional: boolean): string {
  switch (plan) {
    case 'HAZARDS':
      return 'Do the urgent work'
    case 'CORE':
      return 'Recommended'
    case 'ALL':
      return hasOptional ? 'Everything we would do' : 'Recommended'
  }
}

export interface PlanChapterProps {
  document: ProposalDocumentDto
  pricing: PricingBlock
  /**
   * The whole proposal priced once, before any choice: what the figures say
   * when nobody is choosing. Its tax is already irrigation-aware, so nothing
   * here re-taxes a total.
   */
  offered: SelectionOutcome
  choosing: Choosing | null
  assurances: ProseBlock | null
  taxRate: string | null
  homeCount: number | null
}

export function PlanChapter({
  document,
  pricing,
  offered,
  choosing,
  assurances,
  taxRate,
  homeCount,
}: PlanChapterProps) {
  const [adjusting, setAdjusting] = React.useState(false)
  const unpriced = pricing.totalAmount === null
  const hasIrrigation = irrigationScheduleBlocks(document).length > 0

  return (
    <div className="plan">
      {choosing === null ? null : (
        <>
          <PlanCards choosing={choosing} homeCount={homeCount} />
          <div className="plan-adjust print:hidden">
            <div className="plan-adjust-row">
              <button
                type="button"
                className="proposal-link plan-adjust-toggle"
                aria-expanded={adjusting}
                onClick={() => {
                  setAdjusting(!adjusting)
                }}
              >
                Have a budget? Adjust what’s included
              </button>
              {choosing.changes.length === 0 ? null : (
                <span className="plan-count">
                  {choosing.changes.length}{' '}
                  {choosing.changes.length === 1 ? 'change' : 'changes'}
                </span>
              )}
              <span className="proposal-small">{adjustHint(document, hasIrrigation)}</span>
            </div>
            {adjusting ? <AdjustPanel choosing={choosing} /> : null}
          </div>
          <ChangesList choosing={choosing} />
        </>
      )}

      <PricingTable
        document={document}
        pricing={pricing}
        offered={offered}
        selection={choosing?.selection ?? null}
        choosing={choosing}
        taxRate={taxRate}
      />

      {seasonNote(document, pricing)}

      {unpriced ? (
        <p className="proposal-note proposal-danger">
          No labor rate is configured, so this proposal is not yet priced.
        </p>
      ) : null}

      {assurances === null || assurances.bullets.length === 0 ? null : (
        <ul className="plan-assurances" aria-label={assurances.title ?? 'Terms'}>
          {assurances.bullets.map((bullet) => {
            const { heading, detail } = splitHeading(bullet)
            return (
              <li key={bullet}>
                <span className="plan-assurance-head">{heading}</span>
                {detail === null ? null : (
                  <span className="plan-assurance-detail">{detail}</span>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

/**
 * Which visit the price is for, when the work has seasons: the client is
 * approving the next visit, and later work is an estimate under Future work
 * (docs/seasonal-work.md). Nothing when no work has a season.
 */
function seasonNote(
  document: ProposalDocumentDto,
  pricing: PricingBlock,
): React.ReactNode {
  const seasons = pricing.nextSeasons ?? []
  if (seasons.length === 0) return null
  const when = seasons
    .map((season) => SEASON_LABELS[season].toLowerCase())
    .join(' and ')
  const hasLater = document.blocks.some((block) => block.type === 'futureWork')
  return (
    <p className="proposal-note">
      This is the price of the next visit, in {when}.
      {hasLater
        ? ' Work we recommend for later seasons is under Future work, as an estimate.'
        : ''}
    </p>
  )
}

/** The fixed plans, as cards to pick from. */
function PlanCards({
  choosing,
  homeCount,
}: {
  choosing: Choosing
  homeCount: number | null
}) {
  const available = choosing.figures.filter((figure) => figure.isAvailable)
  // One plan is no choice; the totals below already say what it costs.
  if (available.length < 2) return null

  return (
    <div className="plan-cards" role="radiogroup" aria-label="Plans">
      {available.map((figure) => (
        <PlanCard
          key={figure.plan}
          figure={figure}
          hasOptional={choosing.hasOptional}
          isChosen={choosing.state.plan === figure.plan}
          isLocked={choosing.isLocked}
          homeCount={homeCount}
          onChoose={() => {
            choosing.setPlan(figure.plan)
          }}
        />
      ))}
    </div>
  )
}

function PlanCard({
  figure,
  hasOptional,
  isChosen,
  isLocked,
  homeCount,
  onChoose,
}: {
  figure: PlanFigures
  hasOptional: boolean
  isChosen: boolean
  isLocked: boolean
  homeCount: number | null
  onChoose: () => void
}) {
  const now = cents(figure.amountDue)
  return (
    <button
      type="button"
      role="radio"
      aria-checked={isChosen}
      disabled={isLocked}
      className={cn('plan-card', isChosen && 'is-chosen')}
      onClick={onChoose}
    >
      <span className="proposal-tag">{planKicker(figure.plan, hasOptional)}</span>
      <span className="plan-card-name">{planLabel(figure.plan, hasOptional)}</span>
      <span className="plan-card-blurb">{planBlurb(figure, hasOptional)}</span>
      <span className="plan-card-price">{wholeDollars(now)}</span>
      <span className="plan-card-sub">
        {workCount(figure.treeCount, figure.repairCount)}
        {homeCount === null ? '' : ` · ${wholeDollars(now / homeCount)} per home`}
      </span>
    </button>
  )
}

/** The client's changes from the plan, each with what it is worth and an undo. */
function ChangesList({ choosing }: { choosing: Choosing }) {
  if (choosing.changes.length === 0) return null
  return (
    <div className="plan-changes">
      <p className="proposal-label">
        Changes from {planLabel(choosing.state.plan, choosing.hasOptional)} ·{' '}
        {choosing.changes.length}
      </p>
      <ul>
        {choosing.changes.map((change) => (
          <li key={`${change.field}-${String(change.index)}`}>
            <span>{change.text}</span>
            <span className="proposal-mono">{deltaWords(change)}</span>
            {choosing.isLocked ? null : (
              <button
                type="button"
                className="proposal-link print:hidden"
                onClick={() => {
                  choosing.undo(change)
                }}
              >
                Undo
              </button>
            )}
          </li>
        ))}
      </ul>
      {choosing.isLocked ? null : (
        <button
          type="button"
          className="proposal-link print:hidden"
          onClick={choosing.reset}
        >
          Back to {planLabel(choosing.state.plan, choosing.hasOptional)} as offered
        </button>
      )}
    </div>
  )
}

function deltaWords(change: AdjustmentChange): string {
  if (change.deltaCents === 0) return 'no change'
  const sign = change.deltaCents < 0 ? '−' : '+'
  return `${sign}${wholeDollars(Math.abs(change.deltaCents))}`
}

type Tab = 'areas' | 'work' | 'problems' | 'trees' | 'species' | 'irrigation'

const TAB_LABELS: Record<Tab, string> = {
  areas: 'Areas',
  work: 'Kinds of work',
  problems: 'Problems',
  trees: 'Trees',
  species: 'Species',
  irrigation: 'Irrigation',
}

/**
 * The finer controls, closed until asked for.
 *
 * Areas first, because that is how a board splits a budget; species last,
 * because it is the least likely reason anybody has. Kinds of work appear only
 * when the proposal offered them.
 *
 * Every row is counted and priced against what is chosen so far, so leaving
 * out an area leaves its trees out of the problem figures too. A row keeps
 * its place when something broader has taken all of it out, and says so.
 */
export function AdjustPanel({ choosing }: { choosing: Choosing }) {
  const { document, selection } = choosing
  const { sites, areas, jobs, problems, species, kinds, zones } = React.useMemo(
    () => ({
      sites: siteChoices(document, selection),
      areas: areaChoices(document, selection),
      jobs: document.clientMaySelectJobTypes ? jobChoices(document, selection) : [],
      problems: problemChoices(document, selection),
      species: speciesChoices(document, selection),
      kinds: irrigationKindChoices(document, selection),
      zones: zoneChoices(document, selection),
    }),
    [document, selection],
  )
  const hasTrees = scheduleTrees(document).length > 0

  const tabs: Tab[] = [
    ...(sites.length > 1 || areas.length > 0 ? (['areas'] as const) : []),
    ...(jobs.length > 0 ? (['work'] as const) : []),
    ...(problems.length > 0 ? (['problems'] as const) : []),
    ...(hasTrees ? (['trees'] as const) : []),
    ...(species.length > 1 ? (['species'] as const) : []),
    ...(kinds.length > 0 ? (['irrigation'] as const) : []),
  ]
  const [tab, setTab] = React.useState<Tab>(tabs[0] ?? 'irrigation')
  const a = choosing.state.adjustments

  const set = (patch: AdjustmentPatch): void => {
    choosing.adjust(applyAdjustment(a, patch))
  }

  function toggleList<K>(
    list: K[],
    key: K,
    include: boolean,
    same: (x: K, y: K) => boolean,
  ): K[] {
    const rest = list.filter((entry) => !same(entry, key))
    return include ? rest : [...rest, key]
  }

  return (
    <div className="plan-panel">
      <div className="proposal-seg" role="tablist" aria-label="Adjust by">
        {tabs.map((entry) => (
          <button
            key={entry}
            type="button"
            role="tab"
            aria-selected={tab === entry}
            aria-pressed={tab === entry}
            onClick={() => {
              setTab(entry)
            }}
          >
            {TAB_LABELS[entry]}
          </button>
        ))}
      </div>

      <div className="plan-panel-body" role="tabpanel">
        {tab === 'areas' ? (
          <>
            {sites.length > 1 ? (
              <ChoiceList
                legend="Properties"
                choices={sites}
                isIncluded={(key) => !a.excludedSites.includes(key)}
                keyOf={(key) => key}
                disabled={choosing.isLocked}
                onToggle={(key, include) => {
                  set({
                    field: 'excludedSites',
                    value: toggleList(a.excludedSites, key, include, (x, y) => x === y),
                  })
                }}
              />
            ) : null}
            {areas.length === 0 ? null : (
              <ChoiceList
                legend="Areas"
                choices={areas}
                isIncluded={(key) =>
                  !a.excludedAreaNames.some(
                    (entry) => entry.site === key.site && entry.name === key.name,
                  )
                }
                keyOf={(key) => `${key.site}/${key.name}`}
                disabled={choosing.isLocked}
                onToggle={(key, include) => {
                  set({
                    field: 'excludedAreaNames',
                    value: toggleList(
                      a.excludedAreaNames,
                      key,
                      include,
                      (x, y) => x.site === y.site && x.name === y.name,
                    ),
                  })
                }}
              />
            )}
          </>
        ) : null}
        {tab === 'work' ? (
          <ChoiceList
            legend="Kinds of work"
            note="Leaving out a kind of work takes its share off each tree that has it. Getting the crew on site and cleaning up stay in."
            choices={jobs}
            isIncluded={(key) => !a.excludedJobTypes.includes(key)}
            keyOf={(key) => key}
            disabled={choosing.isLocked}
            onToggle={(key, include) => {
              set({
                field: 'excludedJobTypes',
                value: toggleList(a.excludedJobTypes, key, include, (x, y) => x === y),
              })
            }}
          />
        ) : null}
        {tab === 'problems' ? (
          <ChoiceList
            legend="Problems"
            note="Leaving out a problem drops its treatment. The pruning on those trees stays."
            choices={problems}
            isIncluded={(key) => !a.excludedProblems.includes(key)}
            keyOf={(key) => key}
            disabled={choosing.isLocked}
            onToggle={(key, include) => {
              set({
                field: 'excludedProblems',
                value: toggleList(a.excludedProblems, key, include, (x, y) => x === y),
              })
            }}
          />
        ) : null}
        {tab === 'species' ? (
          <ChoiceList
            legend="Species"
            choices={species}
            isIncluded={(key) => !a.excludedSpecies.includes(key)}
            keyOf={(key) => key}
            disabled={choosing.isLocked}
            onToggle={(key, include) => {
              set({
                field: 'excludedSpecies',
                value: toggleList(a.excludedSpecies, key, include, (x, y) => x === y),
              })
            }}
          />
        ) : null}
        {tab === 'trees' ? <TreeChoices choosing={choosing} /> : null}
        {tab === 'irrigation' ? (
          <IrrigationChoices
            choosing={choosing}
            kinds={kinds}
            zones={zones}
            set={set}
            toggleList={toggleList}
          />
        ) : null}
      </div>
    </div>
  )
}

function ChoiceList<K>({
  legend,
  note,
  choices,
  isIncluded,
  keyOf,
  disabled,
  onToggle,
}: {
  legend: string
  note?: string
  choices: Choice<K>[]
  isIncluded: (key: K) => boolean
  keyOf: (key: K) => string
  disabled: boolean
  onToggle: (key: K, include: boolean) => void
}) {
  return (
    <fieldset className="plan-choices">
      <legend className="proposal-label">{legend}</legend>
      {note === undefined ? null : <p className="proposal-small">{note}</p>}
      <ul>
        {choices.map((choice) => {
          const included = isIncluded(choice.key)
          // Already out with something broader: unticking it would change nothing.
          const isMoot = choice.reach === null
          const id = `plan-${legend}-${keyOf(choice.key)}`.replace(/[^A-Za-z0-9_-]/g, '_')
          return (
            <li key={keyOf(choice.key)}>
              <label
                htmlFor={id}
                className={cn('plan-choice', !included && 'is-off', isMoot && 'is-moot')}
              >
                <input
                  id={id}
                  type="checkbox"
                  checked={included}
                  disabled={disabled || (isMoot && included)}
                  onChange={(event) => {
                    onToggle(choice.key, event.target.checked)
                  }}
                />
                <span className="plan-choice-label">{choice.label}</span>
                <span className="plan-choice-reach">{choice.reach ?? 'None included'}</span>
                <ChoiceAmount choice={choice} />
              </label>
            </li>
          )
        })}
      </ul>
    </fieldset>
  )
}

/**
 * What a row is worth. Nothing when it reaches nothing. Where it reaches
 * trees but moves no money, as a problem whose fix is in the pruning, it says
 * so rather than "$0", which reads as free work.
 */
function ChoiceAmount<K>({ choice }: { choice: Choice<K> }) {
  if (choice.reach === null) return <span className="plan-choice-amount" />
  if (choice.cents === 0) {
    return <span className="plan-choice-amount proposal-muted">No separate charge</span>
  }
  return (
    <span className="plan-choice-amount proposal-mono">{wholeDollars(choice.cents)}</span>
  )
}

/** Single trees, searchable, with the same choices as the card on the map. */
function TreeChoices({ choosing }: { choosing: Choosing }) {
  const [query, setQuery] = React.useState('')
  const trees = scheduleTrees(choosing.document)
  const sites = new Set(trees.map((tree) => tree.site))
  const needle = query.trim().toLowerCase().replace(/^#/, '')
  const shown = trees.filter(({ row }) => {
    if (needle === '') return true
    const species = (row.species?.name ?? UNIDENTIFIED_LABEL).toLowerCase()
    return row.label.toLowerCase().replace(/^#/, '').includes(needle) || species.includes(needle)
  })

  return (
    <fieldset className="plan-choices">
      <legend className="proposal-label">Single trees</legend>
      <p className="proposal-small">
        Or pick a tree on the plan above and choose there.
      </p>
      <input
        type="search"
        className="plan-search"
        placeholder="Find by number or species"
        aria-label="Find a tree"
        value={query}
        onChange={(event) => {
          setQuery(event.target.value)
        }}
      />
      <ul className="plan-tree-list">
        {shown.map(({ site, row }) => {
          const status = choosing.treeStatus(site, row)
          const reason = choosing.groupReason(site, row)
          const id = `plan-tree-${site}-${row.label}`.replace(/[^A-Za-z0-9_-]/g, '_')
          return (
            <li key={`${site}/${row.label}`}>
              <label htmlFor={id} className={cn('plan-choice', status === 'out' && 'is-off')}>
                <input
                  id={id}
                  type="checkbox"
                  checked={status !== 'out'}
                  disabled={choosing.isLocked || (reason !== null && status === 'out')}
                  onChange={(event) => {
                    choosing.setTree(site, row, event.target.checked ? 'in' : 'out')
                  }}
                />
                <span className="plan-choice-label">
                  <span className="proposal-mono">{row.label}</span>{' '}
                  {row.species?.name ?? UNIDENTIFIED_LABEL}
                  {sites.size > 1 ? <span className="proposal-muted"> · {site}</span> : null}
                </span>
                <span className="plan-choice-reach">
                  {reason !== null && status === 'out'
                    ? `With ${reason}`
                    : (row.problems[0]?.name ?? '')}
                </span>
                {row.removal === null || status === 'out' ? (
                  <span />
                ) : (
                  <select
                    aria-label={`Prune or remove tree ${row.label}`}
                    className="plan-select"
                    disabled={choosing.isLocked}
                    value={choosing.workChoice(site, row) === 'REMOVE' ? 'remove' : 'prune'}
                    onChange={(event) => {
                      choosing.setTree(site, row, event.target.value === 'remove' ? 'remove' : 'prune')
                    }}
                  >
                    <option value="prune">Prune</option>
                    <option value="remove">Remove</option>
                  </select>
                )}
              </label>
            </li>
          )
        })}
      </ul>
    </fieldset>
  )
}

/**
 * The figures: per area when there is more than one to approve, then the
 * subtotal, tax and total.
 *
 * A single area is not tabled: one row repeating the total says nothing.
 */
function PricingTable({
  document,
  pricing,
  offered,
  selection,
  choosing,
  taxRate,
}: {
  document: ProposalDocumentDto
  pricing: PricingBlock
  offered: SelectionOutcome
  selection: ProposalSelection | null
  choosing: Choosing | null
  taxRate: string | null
}) {
  const outcome = choosing?.outcome ?? null
  const priced = outcome ?? offered
  const subtotal = priced.totalAmount
  // From the outcome, never re-taxed here: irrigation is taxed item by item.
  const taxed = { taxAmount: priced.taxAmount, amountDue: priced.amountDue }
  const rateLabel = taxRateLabel(taxRate)
  const irrigation = irrigationRows(document, priced)
  // An irrigation-only proposal has no tree work to table.
  const treeRows = document.includesTrees ? pricing.sites : []
  const rowCount = treeRows.length + irrigation.length
  const amountFor = (entry: PricingBlock['sites'][number]): string | null => {
    const match = outcome?.sites.find((site) => pricingRowMatches(site, entry))
    return match?.amount ?? entry.amount
  }

  return (
    <div className="plan-figures">
      <table className="proposal-table">
        {rowCount > 1 ? (
          <thead>
            <tr>
              <th>{treeRows.some((entry) => entry.areaName != null) ? 'Area' : 'Property'}</th>
              <th className="proposal-money">Amount</th>
            </tr>
          </thead>
        ) : null}
        {rowCount > 1 ? (
          <tbody>
            {treeRows.map((entry) => (
              <tr key={`${entry.siteName ?? entry.name}:${entry.areaName ?? ''}:${entry.name}`}>
                <td>{irrigation.length > 0 ? `Tree work · ${entry.name}` : entry.name}</td>
                <td className="proposal-money">{formatMoneyAmount(amountFor(entry))}</td>
              </tr>
            ))}
            {irrigation.map((row) => (
              <tr key={`irrigation:${row.siteName}`} className={cn(row.isExcluded && 'is-off')}>
                <td>
                  {row.label}
                  {row.driveNote === null ? null : (
                    <span className="proposal-small"> · {row.driveNote}</span>
                  )}
                </td>
                <td className="proposal-money">{formatMoneyAmount(row.amount)}</td>
              </tr>
            ))}
          </tbody>
        ) : null}
        <tfoot>
          {taxed.taxAmount === null || rateLabel === null ? null : (
            <>
              <tr className="proposal-subtotal">
                <td>Work</td>
                <td className="proposal-money">{formatMoneyAmount(subtotal)}</td>
              </tr>
              <tr className="proposal-subtotal">
                <td>Sales tax ({rateLabel})</td>
                <td className="proposal-money">{formatMoneyAmount(taxed.taxAmount)}</td>
              </tr>
            </>
          )}
          <tr className="proposal-total">
            <td>Total</td>
            <td className="proposal-money">{formatMoneyAmount(taxed.amountDue)}</td>
          </tr>
        </tfoot>
      </table>
      {selection === null || outcome?.deferredAmount == null ? null : (
        <LeftOutNote document={document} selection={selection} />
      )}
    </div>
  )
}

/**
 * What the choices leave out, under the total: recommended work is left for
 * later, and optional work is only not included, since we never said the
 * property needed it.
 */
function LeftOutNote({
  document,
  selection,
}: {
  document: ProposalDocumentDto
  selection: ProposalSelection
}) {
  const { recommended, optional } = React.useMemo(
    () => leftOutWork(document, selection),
    [document, selection],
  )
  let text: string
  if (recommended !== null && optional !== null) {
    text = `${formatMoneyAmount(recommended)} of the recommended work is left for later, and ${formatMoneyAmount(optional)} of optional work is not included. Both stay on record, so they can be quoted again next time.`
  } else if (recommended !== null) {
    text = `${formatMoneyAmount(recommended)} of the recommended work is left for later. It stays on record, so it can be quoted again next time.`
  } else if (optional !== null) {
    text = `${formatMoneyAmount(optional)} of optional work is not included. It stays on record, so it can be quoted again next time.`
  } else {
    return null
  }
  return <p className="proposal-note">{text}</p>
}

/** One irrigation row per site in the pricing table, with its drive charge. */
function irrigationRows(
  document: ProposalDocumentDto,
  outcome: SelectionOutcome,
): {
  siteName: string
  label: string
  amount: string
  driveNote: string | null
  isExcluded: boolean
}[] {
  const blocks = irrigationScheduleBlocks(document)
  const several = new Set(blocks.map((block) => block.siteName)).size > 1
  return outcome.irrigationSites.map((site) => {
    const town = blocks.find((block) => block.siteName === site.siteName)
      ?.rateCard.driveCharge?.town
    return {
      siteName: site.siteName,
      label: several ? `Irrigation repairs · ${site.siteName}` : 'Irrigation repairs',
      amount: site.amount,
      driveNote:
        site.driveCharge === null || town === undefined
          ? null
          : `includes ${formatMoneyAmount(site.driveCharge)} to drive to ${town}`,
      isExcluded: site.isExcluded,
    }
  })
}

/**
 * The irrigation controls: all of it, a kind of repair, or a zone. Never the
 * parts or labor inside a repair, for the reason a tree's solution cannot be
 * declined on its own (see "On the proposal" in irrigation.md).
 */
function IrrigationChoices({
  choosing,
  kinds,
  zones,
  set,
  toggleList,
}: {
  choosing: Choosing
  kinds: Choice<string>[]
  zones: Choice<{ site: string; zone: string }>[]
  set: (patch: AdjustmentPatch) => void
  toggleList: <K>(
    list: K[],
    key: K,
    include: boolean,
    same: (x: K, y: K) => boolean,
  ) => K[]
}) {
  const a = choosing.state.adjustments
  const allOut = a.excludedIrrigation
  return (
    <>
      <label className={cn('plan-choice', allOut && 'is-off')}>
        <input
          type="checkbox"
          checked={!allOut}
          disabled={choosing.isLocked}
          onChange={(event) => {
            set({ field: 'excludedIrrigation', value: !event.target.checked })
          }}
        />
        <span className="plan-choice-label">All irrigation repairs</span>
      </label>
      {allOut ? null : (
        <>
          <ChoiceList
            legend="Kinds of repair"
            note="Leaving out a kind of repair leaves out every one of it, parts and labor together."
            choices={kinds}
            isIncluded={(key) => !a.excludedIrrigationKinds.includes(key)}
            keyOf={(key) => key}
            disabled={choosing.isLocked}
            onToggle={(key, include) => {
              set({
                field: 'excludedIrrigationKinds',
                value: toggleList(
                  a.excludedIrrigationKinds,
                  key,
                  include,
                  (x, y) => x === y,
                ),
              })
            }}
          />
          {zones.length < 2 ? null : (
            <ChoiceList
              legend="Zones"
              choices={zones}
              isIncluded={(key) =>
                !a.excludedZones.some(
                  (entry) => entry.site === key.site && entry.zone === key.zone,
                )
              }
              keyOf={(key) => `${key.site}/${key.zone}`}
              disabled={choosing.isLocked}
              onToggle={(key, include) => {
                set({
                  field: 'excludedZones',
                  value: toggleList(
                    a.excludedZones,
                    key,
                    include,
                    (x, y) => x.site === y.site && x.zone === y.zone,
                  ),
                })
              }}
            />
          )}
        </>
      )}
    </>
  )
}

/**
 * The plan beside the page: what is chosen and what it comes to, always in
 * view on a wide screen.
 */
export function PlanRail({
  choosing,
  pricing,
  offered,
  homeCount,
  validUntil,
  phone,
  canAccept,
}: {
  choosing: Choosing | null
  pricing: PricingBlock | null
  offered: SelectionOutcome
  homeCount: number | null
  validUntil: string | null
  phone: string | null
  canAccept: boolean
}) {
  if (pricing === null) return null
  const priced = choosing?.outcome ?? offered
  const taxed = { taxAmount: priced.taxAmount, amountDue: priced.amountDue }
  const amountDue = cents(taxed.amountDue)
  const changes = choosing?.changes.length ?? 0
  const plan = choosing?.state.plan ?? 'ALL'
  const trees =
    choosing === null
      ? null
      : scheduleTrees(choosing.document).filter(
          (tree) => choosing.treeStatus(tree.site, tree.row) === 'in',
        ).length
  const repairs =
    choosing === null
      ? null
      : scheduleRepairs(choosing.document).filter(
          (entry) => choosing.repairStatus(entry.site, entry.repair) === 'in',
        ).length
  const hasTrees =
    choosing === null ? true : scheduleTrees(choosing.document).length > 0
  const hasRepairs =
    choosing === null ? false : scheduleRepairs(choosing.document).length > 0

  return (
    <aside className="rail" aria-label="Your plan">
      <p className="proposal-label">Your plan</p>
      <p className="rail-plan">
        {planLabel(plan, choosing?.hasOptional ?? false)}
        {changes === 0 ? '' : `, ${String(changes)} ${changes === 1 ? 'change' : 'changes'}`}
      </p>
      <p className="rail-amount">{formatMoneyAmount(taxed.amountDue)}</p>
      <p className="proposal-small">
        {taxed.taxAmount === null ? 'No sales tax' : `Includes ${formatMoneyAmount(taxed.taxAmount)} sales tax`}
        {homeCount === null || amountDue === 0
          ? ''
          : ` · ${wholeDollars(amountDue / homeCount)} per home`}
      </p>
      <dl className="rail-facts">
        {trees === null || !hasTrees ? null : (
          <div>
            <dt>Trees</dt>
            <dd>{trees}</dd>
          </div>
        )}
        {repairs === null || !hasRepairs ? null : (
          <div>
            <dt>Repairs</dt>
            <dd>{repairs}</dd>
          </div>
        )}
        {validUntil === null ? null : (
          <div>
            <dt>Price holds</dt>
            <dd>{formatLongDate(validUntil)}</dd>
          </div>
        )}
      </dl>
      {canAccept ? (
        <a href="#accept" className="proposal-btn proposal-btn--gold proposal-btn--block">
          Accept online
        </a>
      ) : null}
      <a href="#plan" className="proposal-btn proposal-btn--outline proposal-btn--block proposal-btn--sm">
        Change the plan
      </a>
      {phone === null ? null : (
        <p className="proposal-small">
          Questions? Call{' '}
          <a className="proposal-mono" href={`tel:${phone.replace(/[^0-9+]/g, '')}`}>
            {phone}
          </a>
        </p>
      )}
    </aside>
  )
}

/** The bar along the bottom of a phone: the total, and the way to the plan. */
export function PhoneBar({
  choosing,
  pricing,
  offered,
  canAccept,
}: {
  choosing: Choosing | null
  pricing: PricingBlock | null
  offered: SelectionOutcome
  canAccept: boolean
}) {
  if (pricing === null) return null
  const taxed = choosing?.outcome ?? offered
  const plan = choosing?.state.plan ?? 'ALL'
  return (
    <div className="phone-bar print:hidden">
      <span className="phone-bar-total">
        <span className="phone-bar-amount">{formatMoneyAmount(taxed.amountDue)}</span>
        <span className="proposal-small">
          {planLabel(plan, choosing?.hasOptional ?? false)}
        </span>
      </span>
      <a
        href={canAccept ? '#accept' : '#plan'}
        className="proposal-btn proposal-btn--gold proposal-btn--sm"
      >
        {canAccept ? 'Review and accept' : 'See the plan'}
      </a>
    </div>
  )
}

export function formatLongDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  })
}
